/**
 * Environment Configuration with Zod Validation
 *
 * Fail-fast on missing or invalid environment variables.
 * All VITE_ prefixed vars are exposed to the client.
 */

import { z } from "zod"

/**
 * Environment variable schema
 *
 * Required variables will cause build to fail if missing.
 * Optional variables have sensible defaults.
 */
const envSchema = z.object({
  // API Configuration
  // Use full URL like "https://api.example.com/api"
  VITE_BACKEND_URL: z
    .string()
    .default("http://localhost:5000"),

  // Request timeout in milliseconds (falls back to 30000 if not set/invalid)
  VITE_API_TIMEOUT: z.coerce
    .number()
    .int()
    .positive()
    .default(30000)
    .catch(30000),

  // Authentication
  VITE_AUTH_BYPASS: z
    .enum(["true", "false"])
    .default("false")
    .transform((val) => val === "true"),

  // Feature Flags
  VITE_ENABLE_MOCK_API: z
    .enum(["true", "false"])
    .default("false")
    .transform((val) => val === "true"),

  // OIDC / Keycloak
  VITE_OIDC_AUTHORITY: z.string().default("https://keycloak.example.com/realms/bilkent"),
  VITE_OIDC_CLIENT_ID: z.string().default("bpu-frontend"),
  VITE_OIDC_REDIRECT_URI: z.string().default("/auth/callback"),
  VITE_OIDC_POST_LOGOUT_REDIRECT_URI: z.string().default("/"),

  // Application Info
  VITE_APP_NAME: z.string().default("BPU Frontend"),
  VITE_APP_VERSION: z.string().optional(),

  // Build Info (set by CI/CD)
  VITE_BUILD_ID: z.string().optional(),
  VITE_COMMIT_SHA: z.string().optional(),

  // Environment Mode (set by Vite)
  MODE: z.enum(["development", "production", "test", "localhost"]).default("development"),
  DEV: z.boolean().default(true),
  PROD: z.boolean().default(false),
})

/**
 * Inferred type from schema
 */
export type Env = z.infer<typeof envSchema>

/**
 * Parse and validate environment variables
 */
function validateEnv(): Env {
  const rawEnv = {
    VITE_AUTH_BYPASS: import.meta.env.VITE_AUTH_BYPASS,
    VITE_ENABLE_MOCK_API: import.meta.env.VITE_ENABLE_MOCK_API,
    VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
    VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
    VITE_BUILD_ID: import.meta.env.VITE_BUILD_ID,
    VITE_COMMIT_SHA: import.meta.env.VITE_COMMIT_SHA,
    VITE_BACKEND_URL: import.meta.env.VITE_BACKEND_URL,
    VITE_API_TIMEOUT: import.meta.env.VITE_API_TIMEOUT,
    VITE_OIDC_AUTHORITY: import.meta.env.VITE_OIDC_AUTHORITY,
    VITE_OIDC_CLIENT_ID: import.meta.env.VITE_OIDC_CLIENT_ID,
    VITE_OIDC_REDIRECT_URI: import.meta.env.VITE_OIDC_REDIRECT_URI,
    VITE_OIDC_POST_LOGOUT_REDIRECT_URI: import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI,
    MODE: import.meta.env.MODE,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
  }

  const result = envSchema.safeParse(rawEnv)

  if (!result.success) {
    const errors = result.error.flatten().fieldErrors
    const errorMessages = Object.entries(errors)
      .map(([key, msgs]) => `  ${key}: ${msgs?.join(", ")}`)
      .join("\n")

    // Fail fast with clear error message
    throw new Error(
      `Environment validation failed:\n${errorMessages}\n\n` +
        `Please check your .env files and ensure all required variables are set.`
    )
  }

  return result.data
}

/**
 * Validated environment variables
 *
 * Usage:
 * ```ts
 * import { env } from "@/shared/config/env"
 *
 * const apiUrl = env.VITE_BACKEND_URL
 * if (env.DEV) { console.log("Development mode") }
 * ```
 */
export const env = validateEnv()

/**
 * Check if running in development mode
 */
export const isDev = env.DEV

/**
 * Check if running in production mode
 */
export const isProd = env.PROD

/**
 * Check if auth bypass is enabled (for local development without backend)
 */
export const isAuthBypassed = env.VITE_AUTH_BYPASS

/**
 * Get API base URL
 */
export const apiBaseUrl = env.VITE_BACKEND_URL
